import { createSlice } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import utils from "../../utils/utils";
import { dispatch } from "../index";

const navigateTo = "/collections";

const initialState = {
  error: null,
  collections: [],
  collectionObject: {},
  totalPages: 1,
  loading: false,
  limit: 10,
  currentPage: 1,
  filter: "all", // "all", "system", "user"
  search: "",
};

const slice = createSlice({
  name: "collections",
  initialState,
  reducers: {
    hasError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    startLoading(state) {
      state.loading = true;
    },
    stopLoading(state) {
      state.loading = false;
    },
    getSuccess(state, action) {
      const data = action.payload.data;
      state.collections = utils.sortAlphatically(data, "name");
      state.collectionObject = utils.getUniqueObject(data, "id");
      const total = action.payload.totalItems;
      const lim = state.limit;
      let pages = Math.floor(total / lim);
      const remainder = total % lim;
      if (remainder !== 0) {
        pages += 1;
      }
      state.totalPages = pages;
      state.loading = false;
    },
    addSuccess(state) {
      state.loading = false;
    },
    editSuccess(state) {
      state.loading = false;
    },
    deleteSuccess(state, action) {
      state.loading = false;
      state.collections = state.collections.filter((item) => item.id !== action.payload.id);
    },
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    setFilter(state, action) {
      state.filter = action.payload;
      state.currentPage = 1;
    },
    setSearch(state, action) {
      state.search = action.payload;
      state.currentPage = 1;
    },
  },
});

export default slice.reducer;

export function startLoading() { return async () => { dispatch(slice.actions.startLoading()); }; }
export function stopLoading() { return async () => { dispatch(slice.actions.stopLoading()); }; }

export function getCollections() {
  return async (dispatch, getState) => {
    try {
      dispatch(slice.actions.startLoading());
      const { filter, currentPage, limit, search } = getState().collections;
      const page = Math.max(0, (currentPage || 1) - 1);
      const params = new URLSearchParams({
        page: String(page),
        size: String(limit),
        filter,
        search: search || "",
      });
      const result = await axios.get(`/uni/get-all-collections?${params.toString()}`);
      dispatch(slice.actions.getSuccess({ data: result.data.collections, totalItems: result.data.totalItems }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function addCollection(data, navigate) {
  return async () => {
    try {
      const payload = {
        name: data.name,
        library_kind: data.library_kind,
        itemIds: [], // empty for now
        image: null
      };
      const response = await axios.post(`/uni/create-collection`, payload);
      dispatch(slice.actions.addSuccess(response.data));
      if (typeof navigate === "function") {
        navigate(navigateTo);
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function editCollection(id, data, navigate) {
  return async () => {
    try {
      const payload = {
        collectionId: id,
        newName: data.name,
        newImage: data.newImage,
        is_featured: data.is_featured,
        is_featured2: data.is_featured2,
      };
      const response = await axios.post(`/uni/edit-collection`, payload);
      dispatch(slice.actions.editSuccess(response.data));
      if (typeof navigate === "function") {
        navigate(navigateTo);
      }
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function deleteCollection(id) {
  return async () => {
    try {
      await axios.delete(`/uni/delete-collection?collectionId=${id}`);
      dispatch(getCollections());
      dispatch(slice.actions.deleteSuccess({ id }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function setCurrentPage(page) {
  return async () => { dispatch(slice.actions.setCurrentPage(page)); };
}

export function setFilter(filter) {
  return async () => { dispatch(slice.actions.setFilter(filter)); };
}

export function setSearch(search) {
  return async () => { dispatch(slice.actions.setSearch(search)); };
}
